import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Mic, Square, Upload, AlertCircle, CheckCircle2, Settings2, Pause, Play, RefreshCcw, FileAudio, FileText, Loader2, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import api from "../lib/api";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLanguage } from "../context/LanguageContext";

import { ConsentDialog } from "../components/ConsentDialog";

const CONSENT_TEXT = "I confirm that all participants in this conversation have been informed about and have consented to this recording being made for assessment purposes.";

export default function Recording() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [caseData, setCaseData] = useState(null);
  const [hasConsent, setHasConsent] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false); // Legacy checkbox state
  const [showConsentForm, setShowConsentForm] = useState(false);

  // Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [status, setStatus] = useState("idle"); // idle, recording, paused, recorded, uploading, transcribing

  // Previous Recordings
  const [recordings, setRecordings] = useState([]);
  const [loadingRecordings, setLoadingRecordings] = useState(false);
  const [selectedRecordings, setSelectedRecordings] = useState([]);

  // Recording Name Dialog
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [recordingName, setRecordingName] = useState("");
  const [pendingBlob, setPendingBlob] = useState(null);
  const [recordingToDelete, setRecordingToDelete] = useState(null);
  const [showDiscardAlert, setShowDiscardAlert] = useState(false);

  // Manual Transcript
  const [showManualTranscript, setShowManualTranscript] = useState(false);
  const [manualTranscriptText, setManualTranscriptText] = useState("");
  const [submittingManual, setSubmittingManual] = useState(false);
  // Audio device selection
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("default");
  const [loadingDevices, setLoadingDevices] = useState(true);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Visualizer Refs
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const animationFrameRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadCase();
    loadAudioDevices();
    loadRecordings();

    return () => {
      stopTimer();
      cancelAnimationFrame(animationFrameRef.current);
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [caseId]);

  const loadCase = async () => {
    try {
      const [caseRes, consentRes] = await Promise.all([
        api.getCase(caseId),
        api.getConsent(caseId),
      ]);
      setCaseData(caseRes.data);
      setHasConsent(consentRes.data.has_consent);

      if (consentRes.data.has_consent) {
        setConsentChecked(true);
      } else {
        // Automatically show consent form if not signed
        setShowConsentForm(true);
      }
    } catch (error) {
      console.error("Failed to load case:", error);
      toast.error("Failed to load case details");
    }
  };

  const handleConsentComplete = () => {
    setHasConsent(true);
    setConsentChecked(true);
    setShowConsentForm(false);
    toast.success("Consent form signed and recorded");
  };

  const loadRecordings = async () => {
    try {
      setLoadingRecordings(true);
      const res = await api.getRecordings(caseId);
      setRecordings(res.data);
    } catch (error) {
      console.error("Failed to load recordings:", error);
    } finally {
      setLoadingRecordings(false);
    }
  };

  const loadAudioDevices = async () => {
    try {
      setLoadingDevices(true);
      await navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => stream.getTracks().forEach(track => track.stop()));

      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === "audioinput");

      const deviceList = [
        { deviceId: "default", label: "System Default" },
        ...audioInputs
          .filter(device => device.deviceId !== "default")
          .map(device => ({
            deviceId: device.deviceId,
            label: device.label || `${t('microphone')} ${device.deviceId.slice(0, 8)}`
          }))
      ];

      setAudioDevices(deviceList);
    } catch (error) {
      console.error("Failed to enumerate devices:", error);
      toast.error("Could not access audio devices");
      setAudioDevices([{ deviceId: "default", label: "System Default" }]);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File exceeds 10MB limit");
      return;
    }

    setAudioBlob(file);
    setPendingBlob(file);
    setStatus("recorded");
    const simpleName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
    setRecordingName(simpleName);

    // Open name dialog immediately for uploaded files
    setShowNameDialog(true);
    toast.success(`Loaded ${simpleName}`);
  };

  const handleConsentChange = async (checked) => {
    setConsentChecked(checked);
    if (checked && !hasConsent) {
      try {
        await api.recordConsent(caseId, {
          consent_text: CONSENT_TEXT,
          consented: true,
        });
        setHasConsent(true);
        toast.success("Consent recorded");
      } catch (error) {
        console.error("Failed to record consent:", error);
        toast.error("Failed to record consent");
        setConsentChecked(false);
      }
    }
  };

  const startTimer = () => {
    stopTimer();
    timerRef.current = setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const visualize = (stream) => {
    if (!canvasRef.current) return;

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);

    source.connect(analyser);
    analyser.fftSize = 256;

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;
    sourceRef.current = source;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext("2d");

    const draw = () => {
      animationFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      canvasCtx.fillStyle = 'rgb(248, 250, 252)'; // bg-slate-50 matches
      canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / bufferLength) * 2.5;
      let barHeight;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        barHeight = dataArray[i] / 2;

        // Gradient color for bars
        const gradient = canvasCtx.createLinearGradient(0, 0, 0, canvas.height);
        gradient.addColorStop(0, '#3b82f6'); // blue-500
        gradient.addColorStop(1, '#1d4ed8'); // blue-700

        canvasCtx.fillStyle = gradient;
        canvasCtx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);

        x += barWidth + 1;
      }
    };

    draw();
  };

  const startRecording = async () => {
    if (!consentChecked) {
      toast.error("Please confirm consent before recording");
      return;
    }

    try {
      const constraints = {
        audio: selectedDevice === "default"
          ? true
          : { deviceId: { exact: selectedDevice } }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // Visualization
      visualize(stream);

      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setPendingBlob(blob); // Save for name dialog
        setStatus("recorded");

        // Stop stream tracks
        stream.getTracks().forEach((track) => track.stop());
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }

        // Stop visualization
        cancelAnimationFrame(animationFrameRef.current);
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }
      };

      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      setIsPaused(false);
      setStatus("recording");
      setRecordingTime(0);
      startTimer();

      const device = audioDevices.find(d => d.deviceId === selectedDevice);
      toast.success(`Recording started: ${device?.label || "Default microphone"}`);

    } catch (error) {
      console.error("Failed to start recording:", error);
      toast.error("Failed to access microphone. Please check permissions.");
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && isRecording && !isPaused) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      setStatus("paused");
      stopTimer();
      toast.info("Recording paused");
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && isRecording && isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      setStatus("recording");
      startTimer();
      toast.info("Recording resumed");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      stopTimer();
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      stopTimer();
      // Show name dialog after recording stops
      setShowNameDialog(true);
    }
  };

  const handleSaveRecording = async () => {
    if (!pendingBlob) return;

    try {
      setStatus("uploading");
      setShowNameDialog(false);

      const formData = new FormData();
      const file = new File([pendingBlob], `recording_${caseId}.webm`, { type: "audio/webm" });
      formData.append("file", file);
      formData.append("recording_name", recordingName || `Recording ${new Date().toLocaleString()}`);

      const res = await api.uploadAudio(caseId, formData);
      toast.success(`Recording "${res.data.recording_name}" saved`);

      // Reset states
      setPendingBlob(null);
      setRecordingName("");
      setAudioBlob(null);
      setStatus("idle");

      // Reload recordings list
      await loadRecordings();
    } catch (error) {
      console.error("Failed to upload:", error);
      toast.error("Failed to save recording");
      setStatus("recorded");
    }
  };

  const uploadAndTranscribe = async (e, existingAudioId = null) => {
    try {
      let currentAudioId = existingAudioId;

      if (!existingAudioId && audioBlob) {
        setStatus("uploading");
        const formData = new FormData();

        let fileToUpload = audioBlob;
        if (!(audioBlob instanceof File)) {
          fileToUpload = new File([audioBlob], `recording_${caseId}.webm`, { type: "audio/webm" });
        }

        formData.append("file", fileToUpload);
        formData.append("recording_name", recordingName || `Recording ${new Date().toLocaleString()}`);

        const res = await api.uploadAudio(caseId, formData);
        currentAudioId = res.data.audio_id;
        toast.success("Audio uploaded");
        await loadRecordings();
      }

      if (!currentAudioId) return;

      setStatus("transcribing");
      await api.transcribe(caseId, { audioId: currentAudioId });
      toast.success("Transcription complete");
      navigate(`/case/${caseId}/transcript`);

    } catch (error) {
      console.error("Failed to process audio:", error);
      toast.error(error.response?.data?.detail || "Failed to process audio");
      setStatus("recorded");
    } finally {
      setStatus("recorded");
    }
  };

  const handleTranscribeSelected = async () => {
    if (selectedRecordings.length === 0) {
      toast.error("Please select at least one recording");
      return;
    }

    try {
      setStatus("transcribing");
      await api.transcribe(caseId, { audio_ids: selectedRecordings });
      toast.success(`Transcribed ${selectedRecordings.length} recording(s)`);
      setSelectedRecordings([]);
      navigate(`/case/${caseId}/transcript`);
    } catch (error) {
      console.error("Failed to transcribe:", error);
      toast.error(error.response?.data?.detail || "Failed to transcribe");
      setStatus("idle");
    }
  };

  const handleDeleteClick = (audioId) => {
    setRecordingToDelete(audioId);
  };

  const confirmDelete = async () => {
    if (!recordingToDelete) return;

    try {
      await api.deleteAudio(caseId, recordingToDelete);
      toast.success("Recording deleted");
      loadRecordings();
    } catch (error) {
      console.error("Failed to delete recording:", error);
      toast.error("Failed to delete recording");
    } finally {
      setRecordingToDelete(null);
    }
  };

  const toggleRecordingSelection = (recId) => {
    setSelectedRecordings(prev =>
      prev.includes(recId) ? prev.filter(id => id !== recId) : [...prev, recId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedRecordings.length === recordings.length) {
      setSelectedRecordings([]);
    } else {
      setSelectedRecordings(recordings.map(r => r.id));
    }
  };

  const handleManualTranscriptSubmit = async () => {
    if (!manualTranscriptText.trim()) {
      toast.error(t('error_empty_transcript') || "Transcript cannot be empty");
      return;
    }
    try {
      setSubmittingManual(true);
      await api.createManualTranscript(caseId, manualTranscriptText);
      toast.success(t('success_transcript_saved') || "Transcript saved successfully");
      navigate(`/case/${caseId}/transcript`);
    } catch (error) {
      console.error("Failed to save transcript:", error);
      toast.error(t('error_save_transcript') || "Failed to save transcript");
    } finally {
      setSubmittingManual(false);
    }
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  if (!caseData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to={`/case/${caseId}`} data-testid="back-to-case">
              <Button variant="ghost" size="sm" className="rounded-sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('back')}
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-bold text-slate-900 font-['Manrope']" data-testid="recording-title">
                {t('record_conversation')}
              </h1>
              <p className="text-sm text-slate-500">{caseData.client_name} ({caseData.client_id})</p>
            </div>
            <div className="ml-auto">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">

        {/* Device & Consent (Only show if not recording) */}
        {!isRecording && status !== "recorded" && (
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-slate-200 rounded-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-['Manrope'] flex items-center gap-2">
                  <Settings2 className="w-4 h-4 text-slate-500" />
                  {t('microphone')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Select
                  value={selectedDevice}
                  onValueChange={setSelectedDevice}
                  disabled={loadingDevices}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder={t('select_device')} />
                  </SelectTrigger>
                  <SelectContent>
                    {audioDevices.map((device) => (
                      <SelectItem key={device.deviceId} value={device.deviceId}>
                        {device.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            <Card className="border-slate-200 rounded-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-['Manrope'] flex items-center gap-2">
                  {hasConsent ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertCircle className="w-4 h-4 text-amber-500" />}
                  {t('consent')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-3">
                  {hasConsent ? (
                    <Button
                      onClick={() => api.downloadConsent(caseId)}
                      variant="outline"
                      className="w-full border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Consent Signed (Download)
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setShowConsentForm(true)}
                      variant="outline"
                      className="w-full border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Sign Consent Form
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <ConsentDialog
          open={showConsentForm}
          onOpenChange={setShowConsentForm}
          caseData={caseData}
          onComplete={handleConsentComplete}
        />

        {/* Recording Card */}
        <Card className="border-slate-200 rounded-sm shadow-sm">
          <CardHeader className="pb-4 border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="text-lg font-['Manrope'] flex justify-between items-center">
              <span>{t('recorder')}</span>
              <span className={`text-sm font-mono px-3 py-1 rounded-full ${isRecording ? 'bg-red-100 text-red-600' : 'bg-slate-200 text-slate-600'}`}>
                {status === "recording" ? t('status_recording') : status === "paused" ? t('status_paused') : status === "recorded" ? t('status_saved') : t('status_ready')}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center">

              {/* Timer */}
              <div className="text-6xl font-bold text-slate-900 font-mono tracking-wider mb-6">
                {formatTime(recordingTime)}
              </div>

              {/* Waveform Canvas */}
              {status === "recording" && (
                <canvas
                  ref={canvasRef}
                  width={300}
                  height={60}
                  className="w-full max-w-md h-[60px] mb-8 bg-slate-50 rounded border border-slate-200"
                />
              )}

              {/* Controls */}
              <div className="flex items-center gap-4 mb-4">
                {status === "idle" && (
                  <Button
                    onClick={startRecording}
                    disabled={!consentChecked}
                    className="bg-red-600 hover:bg-red-700 text-white rounded-full w-16 h-16 shadow-lg transition-transform hover:scale-105 active:scale-95 flex items-center justify-center p-0"
                  >
                    <Mic className="w-6 h-6" />
                  </Button>
                )}

                {(status === "recording" || status === "paused") && (
                  <>
                    {status === "recording" ? (
                      <Button
                        onClick={pauseRecording}
                        variant="outline"
                        className="rounded-full w-12 h-12 border-2 border-slate-300 hover:border-slate-400 p-0"
                        title="Pause"
                      >
                        <Pause className="w-5 h-5 text-slate-700" />
                      </Button>
                    ) : (
                      <Button
                        onClick={resumeRecording}
                        variant="outline"
                        className="rounded-full w-12 h-12 border-2 border-emerald-300 hover:border-emerald-400 bg-emerald-50 p-0"
                        title="Resume"
                      >
                        <Play className="w-5 h-5 text-emerald-600" />
                      </Button>
                    )}

                    <Button
                      onClick={handleStopRecording}
                      className="bg-slate-900 hover:bg-slate-800 text-white rounded-full w-16 h-16 shadow-lg transition-transform hover:scale-105 active:scale-95 flex items-center justify-center p-0"
                      title="Stop"
                    >
                      <Square className="w-6 h-6 fill-current" />
                    </Button>
                  </>
                )}
              </div>

              {status === "idle" && (
                <div className="flex flex-col items-center gap-4 mt-2">
                  <p className="text-sm text-slate-500">{t('click_to_start')}</p>

                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400 uppercase tracking-widest font-semibold">{t('or')}</span>
                  </div>

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    className="hidden"
                    accept="audio/*,.mp3,.wav,.m4a,.mp4,.webm"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50 text-slate-600"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {t('upload_audio')}
                  </Button>
                  <p className="text-[10px] text-slate-400 mb-2">{t('upload_help')}</p>

                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-slate-400 uppercase tracking-widest font-semibold">{t('or')}</span>
                  </div>

                  <Button
                    variant="outline"
                    onClick={() => setShowManualTranscript(true)}
                    className="border-dashed border-slate-300 hover:border-slate-400 text-slate-600 mt-2"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {t('enter_manual_transcript') || "Upload Text Transcript"}
                  </Button>
                </div>
              )}

              {/* Post-Recording Actions */}
              {status === "recorded" && (
                <div className="w-full max-w-md space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="bg-slate-100 p-4 rounded-md flex flex-col gap-2">
                    <label className="text-xs font-semibold text-slate-500 uppercase">{t('preview')}</label>
                    {audioBlob && (
                      <audio controls src={URL.createObjectURL(audioBlob)} className="w-full h-10" />
                    )}
                  </div>

                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => setShowDiscardAlert(true)}
                      className="flex-1"
                    >
                      <RefreshCcw className="w-4 h-4 mr-2" />
                      {t('discard')}
                    </Button>
                    <Button
                      onClick={uploadAndTranscribe}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {t('process_recording')}
                    </Button>
                  </div>
                </div>
              )}

              {(status === "uploading" || status === "transcribing") && (
                <div className="flex flex-col items-center gap-3 mt-4">
                  <div className="w-8 h-8 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
                  <span className="text-sm font-medium text-slate-700">
                    {status === "uploading" ? t('status_uploading') : t('status_transcribing')}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Previous Recordings */}
        {recordings.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
                <FileAudio className="w-5 h-5 text-slate-500" />
                {t('uploaded_recordings')} ({recordings.length})
              </h3>
              <div className="flex items-center gap-2">
                {recordings.length > 1 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={toggleSelectAll}
                    className="rounded-sm"
                  >
                    {selectedRecordings.length === recordings.length ? t('deselect_all') : t('select_all')}
                  </Button>
                )}
                {selectedRecordings.length > 0 && (
                  <Button
                    size="sm"
                    onClick={handleTranscribeSelected}
                    disabled={status === "transcribing" || status === "uploading"}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm"
                  >
                    {status === "transcribing" ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('status_transcribing')}
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        {t('transcribe_selected')} ({selectedRecordings.length})
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
            <div className="grid gap-3">
              {recordings.map((rec) => (
                <Card key={rec.id} className={`border-slate-200 rounded-sm ${selectedRecordings.includes(rec.id) ? "ring-2 ring-blue-500" : ""}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <Checkbox
                      checked={selectedRecordings.includes(rec.id)}
                      onCheckedChange={() => toggleRecordingSelection(rec.id)}
                      className="flex-shrink-0"
                    />
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <FileAudio className="w-5 h-5 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {rec.recording_name || rec.file_name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {new Date(rec.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <audio
                        controls
                        src={rec.url}
                        className="h-8 max-w-[200px]"
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => uploadAndTranscribe(null, rec.id)}
                        disabled={status === "transcribing" || status === "uploading"}
                        title="Transcribe this recording only"
                      >
                        <FileText className="w-4 h-4 text-slate-600" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteClick(rec.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete recording"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Recording Name Dialog */}
      <Dialog open={showNameDialog} onOpenChange={setShowNameDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('save_recording_title')}</DialogTitle>
            <DialogDescription>
              {t('save_recording_desc')}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="recording-name" className="text-sm font-medium">
              {t('recording_name_label')}
            </Label>
            <Input
              id="recording-name"
              value={recordingName}
              onChange={(e) => setRecordingName(e.target.value)}
              placeholder={`Recording ${new Date().toLocaleString()}`}
              className="mt-2"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSaveRecording();
                }
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowNameDialog(false);
                setRecordingName("");
                setPendingBlob(null);
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleSaveRecording}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {t('save_recording_title')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!recordingToDelete} onOpenChange={(open) => !open && setRecordingToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('delete_recording_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('delete_recording_desc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Discard Confirmation Dialog */}
      <AlertDialog open={showDiscardAlert} onOpenChange={setShowDiscardAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('discard_recording_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('discard_recording_desc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setAudioBlob(null);
                setPendingBlob(null);
                setStatus("idle");
                setRecordingTime(0);
                setShowDiscardAlert(false);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('discard')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manual Transcript Dialog */}
      <Dialog open={showManualTranscript} onOpenChange={setShowManualTranscript}>
        <DialogContent className="max-w-2xl bg-white p-0 overflow-hidden border-0 shadow-lg rounded-lg">
          <DialogHeader className="p-6 pb-2 border-b border-slate-100 bg-slate-50/50">
            <DialogTitle className="text-xl font-['Manrope'] text-slate-800">
              {t('enter_manual_transcript') || "Upload Text Transcript"}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {t('manual_transcript_desc') || "Provide the text of the conversation. Our AI will securely process this transcript to evaluate the client against the Self-Sufficiency Matrix."}
            </DialogDescription>
          </DialogHeader>
          <div className="p-6">
            <textarea
              value={manualTranscriptText}
              onChange={(e) => setManualTranscriptText(e.target.value)}
              placeholder={t('transcript_placeholder') || "Enter the conversation text here..."}
              className="w-full h-[300px] p-4 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none font-medium leading-relaxed"
            />
          </div>
          <DialogFooter className="p-6 pt-4 border-t border-slate-100 bg-slate-50/50 flex justify-between items-center sm:justify-between">
            <Button variant="ghost" className="text-slate-500 hover:bg-slate-200 hover:text-slate-700" onClick={() => setShowManualTranscript(false)}>
              {t('cancel') || "Cancel"}
            </Button>
            <Button
              onClick={handleManualTranscriptSubmit}
              disabled={submittingManual || !manualTranscriptText.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-6 shadow-sm flex items-center gap-2"
            >
              {submittingManual && <Loader2 className="w-4 h-4 animate-spin" />}
              {submittingManual ? (t('saving') || "Processing...") : (t('proceed') || "Process Transcript")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
